/* eslint-disable react/jsx-props-no-spreading */
/** @jsx jsx */
import React, { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';

import { css, jsx } from '@compiled/react';

// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import { ExitingPersistence, FadeIn } from '@atlaskit/motion';
import { Box, Inline, Stack, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import type { PopupComponentProps } from '@atlaskit/popup';
import CrossIcon from '@atlaskit/icon/core/cross';
import Tooltip from '@atlaskit/tooltip';
import { fg } from '@atlassian/jira-feature-gating';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	UNSAFE_DO_NOT_USE_BANNER_HEIGHT,
	UNSAFE_DO_NOT_USE_TOP_NAVIGATION_HEIGHT,
} from '@atlassian/jira-navigation-system/src/index.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import {
	fireUIAnalytics,
	useAnalyticsEvents,
	DRAWER,
	ContextualAnalyticsData,
	FireScreenAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useProjectCreateDrawer } from '@atlassian/jira-project-create-drawer-context/src/index.tsx';
import type { ui_projectsDirectoryTemplatesSidebar_RecommendationsInProjectList$key } from '@atlassian/jira-relay/src/__generated__/ui_projectsDirectoryTemplatesSidebar_RecommendationsInProjectList.graphql';
import { useEnvironment } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';
import { useFireCrossFlowAnalyticsEvents } from '@atlassian/surface-analytics';
import {
	PACKAGE_NAME,
	TEAM_NAME,
	PROJECT_CREATE_SOURCE,
	SIDEBAR_WIDTH,
	getProductTypeCcpReferenceIdMapForEnv,
} from '../constants.tsx';
import { messages } from '../messages.tsx';
import { useSetProjectListRightPanelState } from '../services/set-right-panel-state/index.tsx';
import { TemplateCard } from './card/index.tsx';
import { TemplatePreview } from './preview/index.tsx';
import { JiraProjectListSidePanelSkipLink } from './skip-link/index.tsx';

type Props = {
	rootQueryRef: ui_projectsDirectoryTemplatesSidebar_RecommendationsInProjectList$key;
	refreshEnabled: boolean;
};

const RecommendationsInProjectList = ({ rootQueryRef, refreshEnabled }: Props) => {
	const [templatePreviewKey, setTemplatePreviewKey] = useState<string | null>(null);
	const environment = useEnvironment();
	const { fireViewedUIEvent } = useFireCrossFlowAnalyticsEvents();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const {
		methods: { open: openProjectCreate },
	} = useProjectCreateDrawer();
	const buttonEvent = createAnalyticsEvent({
		action: 'clicked',
		actionSubject: 'button',
	});
	const { formatMessage } = useIntl();

	const { projectListViewTemplates, userPreferences } =
		useFragment<ui_projectsDirectoryTemplatesSidebar_RecommendationsInProjectList$key>(
			// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
			graphql`
				fragment ui_projectsDirectoryTemplatesSidebar_RecommendationsInProjectList on JiraQuery {
					projectListViewTemplates(cloudId: $cloudId, experimentKey: "sidebarExperiment")
						@optIn(to: "JiraProjectListViewTemplate") {
						nodes {
							key @required(action: THROW)
							templateType @required(action: THROW)
							productKey @required(action: THROW)
							isProductLicensed @required(action: THROW)
							...card_projectsDirectoryTemplatesSidebar_TemplateCard
							...preview_projectsDirectoryTemplatesSidebar_TemplatePreview
						}
					}
					userPreferences(cloudId: $cloudId) @optIn(to: "JiraUserPreferences") {
						__id
					}
				}
			`,
			rootQueryRef,
		);

	const setProjectListRightPanelState = useSetProjectListRightPanelState(userPreferences?.__id);

	const onCloseClick = useCallback(() => {
		fireUIAnalytics(buttonEvent, 'closeButton');
		setProjectListRightPanelState('CLOSED');
	}, [buttonEvent, setProjectListRightPanelState]);

	const templates = useMemo(
		() =>
			projectListViewTemplates?.nodes?.filter(
				<T,>(template: T | undefined | null): template is T => !!template,
			) ?? [],
		[projectListViewTemplates?.nodes],
	);

	const templatePreviewRef = useMemo(
		() => templates.find((template) => template.key === templatePreviewKey),
		[templatePreviewKey, templates],
	);

	const recommendedProductIds = useMemo(
		() =>
			Array.from(
				templates.reduce((acc, template) => {
					const { isProductLicensed, productKey } = template;
					if (!isProductLicensed) {
						acc.add(getProductTypeCcpReferenceIdMapForEnv(environment)[productKey]);
					}
					return acc;
				}, new Set<string>()),
			),
		[environment, templates],
	);

	const RefreshCloseButton = (
		<Tooltip content={formatMessage(messages.closeButtonTooltip)}>
			{(tooltipProps) => (
				<Button
					{...tooltipProps}
					iconBefore={<CrossIcon spacing="spacious" label="" />}
					aria-label={formatMessage(messages.closeButton)}
					appearance="subtle"
					onClick={onCloseClick}
				/>
			)}
		</Tooltip>
	);

	const onMoreTemplatesClick = useCallback(() => {
		fireUIAnalytics(buttonEvent, 'moreTemplatesButton');
		openProjectCreate({ showExperienceSelection: false, source: PROJECT_CREATE_SOURCE });
	}, [buttonEvent, openProjectCreate]);

	useEffect(() => {
		recommendedProductIds.length > 0 &&
			fireViewedUIEvent(
				{
					action: 'viewed',
					actionSubject: 'section',
					actionSubjectId: 'projectsDirectorySidebar',
				},
				{
					recommendedProductIds,
					source: 'projectsDirectorySidebarSection',
				},
				{
					sidebarVisualRefreshEnabled: refreshEnabled,
				},
			);
	}, [fireViewedUIEvent, recommendedProductIds, refreshEnabled]);

	/**
	 * The future state for Nav 4 is that the panel slot will have its own border,
	 * and panels should not apply custom borders.
	 */
	const shouldUsePlatformPanelBorder = refreshEnabled;

	/**
	 * When using the platform panel border, we need to offset the popup by 1 pixel or it covers the panel border.
	 */
	const offset: [number, number] = shouldUsePlatformPanelBorder ? [0, 1] : [0, 0];

	return (
		<Popup
			isOpen
			placement="left-start"
			offset={offset}
			autoFocus={false}
			popupComponent={refreshEnabled ? CustomPopupContainer : undefined}
			messageId="projects-directory-templates-sidebar.ui.popup"
			messageType="transactional"
			trigger={(triggerProps) => (
				<div {...triggerProps} aria-expanded={undefined}>
					<Box
						id="jira-project-list-side-panel"
						xcss={[
							refreshEnabled
								? ExperimentSidebarContentStylesVariation
								: ExperimentSidebarContentStyles,
							refreshEnabled &&
								!shouldUsePlatformPanelBorder &&
								ExperimentSidebarContentBorderStylesVariation,
							/**
							 * Changing to 100% width instead of a fixed width for the panel content,
							 * otherwise enabling the platform panel border can introduce horizontal scrolling.
							 */
							ExperimentSidebarContentWidthStylesVariation,
						]}
						{...(fg('jfp_a11y_autodev_fix_templates_dialog_role')
							? {
									role: 'dialog',
									'aria-modal': 'false',
									'aria-labelledby': 'templates-dialog-title',
								}
							: {})}
					>
						<Stack xcss={refreshEnabled && headerBoxStyles}>
							<Inline
								xcss={refreshEnabled ? TitleStylesVariation : TitleStyles}
								alignBlock="center"
								spread="space-between"
							>
								<Heading
									size={refreshEnabled ? 'small' : 'xsmall'}
									id={
										fg('jfp_a11y_autodev_fix_templates_dialog_role')
											? 'templates-dialog-title'
											: undefined
									}
									as={fg('jfp-a11y-team_autodev_header_fixes') ? 'h2' : undefined}
								>
									{formatMessage(messages.title)}
								</Heading>
								<Box xcss={!refreshEnabled && ButtonBoxStyles}>
									{refreshEnabled ? (
										RefreshCloseButton
									) : (
										<Button
											iconBefore={<CrossIcon spacing="spacious" label="" />}
											aria-label={formatMessage(messages.closeButton)}
											data-testId="projects-directory-templates-sidebar.ui.close-templates-panel"
											appearance="subtle"
											onClick={onCloseClick}
										/>
									)}
								</Box>
							</Inline>
							<Box as="p" xcss={SubtitleStyles}>
								{formatMessage(
									fg('project_term_refresh_ga') ? messages.subtitleGalaxia : messages.subtitle,
								)}
							</Box>
						</Stack>
						<Stack as="ul" xcss={TemplateCardStackStyles}>
							{templates.map((cardRef, index) => (
								<TemplateCard
									key={cardRef.key}
									index={index}
									dataRef={cardRef}
									recommendedProductIds={recommendedProductIds}
									setTemplatePreviewKey={setTemplatePreviewKey}
									refreshEnabled={refreshEnabled}
								/>
							))}
						</Stack>
						<Box xcss={refreshEnabled && moreTemplateLinkStyles}>
							<Button
								appearance="link"
								spacing="none"
								label={formatMessage(messages.moreTemplatesLink)}
								onClick={() => onMoreTemplatesClick()}
							>
								{formatMessage(messages.moreTemplatesLink)}
							</Button>
						</Box>
						<FireScreenAnalytics
							attributes={{
								templates: templates.map(({ key }) => key),
								templateTypes: templates.map(({ templateType }) => templateType),
								sidebarVisualRefreshEnabled: refreshEnabled,
							}}
						/>
						<JiraProjectListSidePanelSkipLink />
					</Box>
				</div>
			)}
			content={({ update }) => (
				<JSErrorBoundary
					id="template-preview-wrapper"
					packageName={PACKAGE_NAME}
					teamName={TEAM_NAME}
					attributes={{ sidebarVisualRefreshEnabled: refreshEnabled }}
				>
					<ExitingPersistence>
						{templatePreviewRef && (
							<FadeIn duration="medium">
								{(fadeInProps) => (
									<div {...fadeInProps}>
										<TemplatePreview
											update={update}
											previewRef={templatePreviewRef}
											refreshEnabled={refreshEnabled}
										/>
									</div>
								)}
							</FadeIn>
						)}
					</ExitingPersistence>
				</JSErrorBoundary>
			)}
		/>
	);
};

const RecommendationsInProjectListContainer = (props: Props) => (
	<ContextualAnalyticsData sourceName="projectListTemplates" sourceType={DRAWER}>
		<RecommendationsInProjectList {...props} />
	</ContextualAnalyticsData>
);

const TitleStyles = xcss({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	textTransform: 'uppercase',
	color: 'color.text.subtle',
	position: 'relative',
});

const ButtonBoxStyles = xcss({
	paddingInline: 'space.050',
});

const SubtitleStyles = xcss({
	color: 'color.text.subtlest',
	font: token('font.body'),
	marginTop: 'space.0',
});

const ExperimentSidebarContentStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `calc(100vh - ${UNSAFE_DO_NOT_USE_TOP_NAVIGATION_HEIGHT} - ${UNSAFE_DO_NOT_USE_BANNER_HEIGHT})`,
	overflow: 'auto',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${SIDEBAR_WIDTH}px`,
	padding: 'space.300',
	backgroundColor: 'elevation.surface.sunken',
	boxSizing: 'border-box',
});

// GRW-1532

const CustomPopupContainer = forwardRef<HTMLDivElement, PopupComponentProps>(
	({ children, ...props }, ref) => (
		<div css={containerStyles} {...props} ref={ref}>
			{children}
		</div>
	),
);

const containerStyles = css({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginBlockStart: '1px', // added to prevent the preview from covering the top border as per designs
	display: 'block',
	boxSizing: 'border-box',
	'&:focus': {
		outline: 'none',
	},
});

const moreTemplateLinkStyles = xcss({
	height: '32px',
	display: 'flex',
	alignItems: 'center',
	marginBlockStart: 'space.0',
	paddingInlineStart: 'space.200',
});

const headerBoxStyles = xcss({
	paddingInlineStart: 'space.200',
	paddingInlineEnd: 'space.100',
	marginBlockEnd: 'space.200',
});

const TitleStylesVariation = xcss({
	color: 'color.text',
	position: 'relative',
});

const ExperimentSidebarContentStylesVariation = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `calc(100vh - ${UNSAFE_DO_NOT_USE_TOP_NAVIGATION_HEIGHT} - ${UNSAFE_DO_NOT_USE_BANNER_HEIGHT})`,
	width: '400px',
	overflow: 'auto',
	paddingBlock: 'space.300',
	paddingInline: 'space.100',
	backgroundColor: 'elevation.surface',
	boxSizing: 'border-box',
});

/**
 * The panel content does not need a hardcoded width, because the panel slot determines width.
 * The content should fill the slot.
 */
const ExperimentSidebarContentWidthStylesVariation = xcss({
	width: '100%',
});

const ExperimentSidebarContentBorderStylesVariation = xcss({
	borderLeftWidth: 'border.width',
	borderLeftStyle: 'solid',
	borderLeftColor: 'color.border',
});

const TemplateCardStackStyles = xcss({
	listStyle: 'none',
	justifyContent: 'start',
	padding: 'space.0',
	marginBlock: 'space.050',
});

export default RecommendationsInProjectListContainer;
